import { TASK_REMIND_DATE_ONLY, TASK_REMIND_TIMED, type TaskRemind } from '@/api/types';
import { formatDueTime } from './due';

/**
 * A task's reminder (ADR 0073), relative to its due date: the choices a timed
 * task offers, the ones a date-only task offers, and the server's mapping
 * between the two when the due kind changes.
 *
 * The mapping lives here once, for the details form (which maps the chosen
 * reminder as the person switches between "a time" and "no time", so what
 * the form shows is what Save will store) and for the mock (which applies it
 * to a PATCH that changes the due kind and names no reminder, as the server
 * does).
 */

/** How a choice reads in the picker. 09:00 is written in the chosen locale, so it is read per call. */
export function remindLabel(remind: TaskRemind): string {
  switch (remind) {
    case 'at_time':
      return 'At due time';
    case '10m_before':
      return '10 minutes before';
    case '1h_before':
      return '1 hour before';
    case '1d_before':
      return '1 day before';
    case 'day_of_9am':
      return `On the day at ${formatDueTime('09:00')}`;
    case 'day_before_9am':
      return `The day before at ${formatDueTime('09:00')}`;
  }
}

/** The choices that fit a due date with (`timed`) or without a time. */
export function remindChoices(timed: boolean): readonly TaskRemind[] {
  return timed ? TASK_REMIND_TIMED : TASK_REMIND_DATE_ONLY;
}

export function isTimedRemind(remind: TaskRemind): boolean {
  return (TASK_REMIND_TIMED as readonly string[]).includes(remind);
}

/**
 * The server's mapping when the due kind changes and the reminder is not
 * named: at the time / 10 min / 1 h before → 09:00 on the day; 1 day before →
 * 09:00 the day before; and back again the other way.
 */
export function mapRemindForDueKind(remind: TaskRemind, timed: boolean): TaskRemind {
  if (timed) {
    if (remind === 'day_of_9am') return 'at_time';
    if (remind === 'day_before_9am') return '1d_before';
    return remind;
  }
  if (remind === '1d_before') return 'day_before_9am';
  if (remind === 'at_time' || remind === '10m_before' || remind === '1h_before') return 'day_of_9am';
  return remind;
}
